import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { MapPinIcon, UserCircleIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { usePortalAuth } from "../context/PortalAuthContext";
import { portalApi } from "../services/portalApi";
import { selectClsLg } from "../constants/formClasses";
import PortalSelect from "../components/PortalSelect";
import { PortalShell } from "../components/PortalShell";
import MexicoLocationSelects from "../components/admin/MexicoLocationSelects";
import BookingMonthPicker from "../components/booking/BookingMonthPicker";
import TimeSlotGrid from "../components/booking/TimeSlotGrid";
import { formatMxLong } from "../utils/dateMx";
import {
  collectInPersonPracticeLocations,
  formatMexicoLocation,
  getRegisteredMunicipalityCodes,
  getRegisteredStateCodes,
  psychPracticeMatchesPatientCity,
} from "../utils/mexicoLocations";
import {
  CM_BOTH,
  CM_IN_PERSON,
  CM_VIRTUAL,
  labelAppointmentModality,
  labelConsultationModesSetting,
  psychSupportsAppointmentModality,
} from "../utils/consultationModes";

// ── Step indicator ────────────────────────────────────────────────────────────
function StepBadge({ n, label, done }) {
  return (
    <div
      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
        done
          ? "bg-brand-navy/10 border-brand-navy/25 text-brand-navy"
          : "bg-white border-brand-lavender/20 text-brand-gray"
      }`}
    >
      <span
        className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${
          done ? "bg-brand-navy text-white" : "bg-brand-beige text-brand-gray"
        }`}
      >
        {n}
      </span>
      {label}
    </div>
  );
}

// ── Section card ──────────────────────────────────────────────────────────────
function SectionCard({ children, className = "" }) {
  return (
    <div className={`rounded-2xl border border-brand-lavender/15 bg-white shadow-sm p-5 sm:p-6 ${className}`}>
      {children}
    </div>
  );
}

// ── Success modal ─────────────────────────────────────────────────────────────
function SuccessModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-sm rounded-2xl bg-white shadow-2xl p-8 text-center space-y-4">
        <span className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-brand-beige mx-auto">
          <CheckCircleIcon className="h-8 w-8 text-brand-navy" aria-hidden />
        </span>
        <h2 className="text-xl font-semibold text-brand-dark">Cita confirmada</h2>
        <p className="text-sm text-brand-gray leading-relaxed">
          Tu cita quedó registrada. Recibirás un correo de confirmación con los detalles.
          Tu profesional puede contactarte para coordinar la sesión.
        </p>
        <p className="text-sm text-brand-gray">
          Puedes revisar o cancelar tu cita en «Mis citas».
        </p>
        <button
          onClick={onClose}
          className="w-full rounded-xl bg-brand-navy py-3 text-sm font-semibold text-white hover:brightness-110 transition-all"
        >
          Aceptar
        </button>
      </div>
    </div>
  );
}

export default function PortalBookAppointment() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = usePortalAuth();
  const [psychs, setPsychs] = useState([]);
  const [psychId, setPsychId] = useState("");
  const [date, setDate] = useState("");
  const [slotOptions, setSlotOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPsychs, setLoadingPsychs] = useState(true);
  const [booking, setBooking] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [modality, setModality] = useState("");
  const [patientLocation, setPatientLocation] = useState({ stateCode: "", municipalityCode: "" });
  const [successOpen, setSuccessOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    portalApi
      .listPsychologists()
      .then((r) => { if (!cancelled) setPsychs(r.psychologists || []); })
      .catch(() => toast.error("No se pudo cargar la lista de psicólogos"))
      .finally(() => { if (!cancelled) setLoadingPsychs(false); });
    return () => { cancelled = true; };
  }, []);

  const selectedPsych = useMemo(() => psychs.find((p) => p.psychologistId === psychId), [psychs, psychId]);
  const registeredPracticeLocations = useMemo(() => collectInPersonPracticeLocations(psychs), [psychs]);
  const registeredStateCodes = useMemo(() => getRegisteredStateCodes(registeredPracticeLocations), [registeredPracticeLocations]);
  const registeredMunicipalityCodes = useMemo(
    () => getRegisteredMunicipalityCodes(registeredPracticeLocations, patientLocation.stateCode),
    [registeredPracticeLocations, patientLocation.stateCode]
  );

  const filteredPsychs = useMemo(() => {
    if (!modality) return [];
    let list = psychs.filter((p) => psychSupportsAppointmentModality(p.consultationModes, modality));
    if (modality === CM_IN_PERSON) {
      const { stateCode, municipalityCode } = patientLocation;
      if (!stateCode || !municipalityCode) return [];
      list = list.filter((p) => psychPracticeMatchesPatientCity(p.location, stateCode, municipalityCode));
    }
    return list;
  }, [psychs, modality, patientLocation]);

  useEffect(() => {
    setPsychId("");
    setDate("");
    setSelectedSlot(null);
    if (modality !== CM_IN_PERSON) setPatientLocation({ stateCode: "", municipalityCode: "" });
  }, [modality]);

  useEffect(() => {
    setPsychId("");
    setDate("");
    setSelectedSlot(null);
  }, [patientLocation.stateCode, patientLocation.municipalityCode]);

  useEffect(() => {
    if (modality !== CM_IN_PERSON || loadingPsychs) return;
    const { stateCode, municipalityCode } = patientLocation;
    if (!municipalityCode || !stateCode) return;
    const allowed = registeredPracticeLocations.get(stateCode);
    if (allowed && !allowed.has(municipalityCode)) setPatientLocation({ stateCode, municipalityCode: "" });
  }, [modality, loadingPsychs, patientLocation, registeredPracticeLocations]);

  useEffect(() => { setSelectedSlot(null); }, [psychId, date]);

  useEffect(() => {
    if (!psychId || !modality) return;
    const p = psychs.find((x) => x.psychologistId === psychId);
    if (p && !psychSupportsAppointmentModality(p.consultationModes, modality)) setPsychId("");
  }, [psychs, psychId, modality]);

  useEffect(() => {
    if (!psychId || !date) { setSlotOptions([]); return; }
    setLoading(true);
    portalApi
      .availability(psychId, date)
      .then((r) => {
        if (r.slotOptions?.length) {
          setSlotOptions(r.slotOptions);
        } else {
          setSlotOptions((r.slots || []).map((startUtc) => ({ startUtc, available: true })));
        }
      })
      .catch((e) => toast.error(e.message || "No se pudieron cargar horarios"))
      .finally(() => setLoading(false));
  }, [psychId, date]);

  function handleSuccessClose() {
    setSuccessOpen(false);
    navigate("/mis-citas", { replace: true });
  }

  async function confirmBooking() {
    if (!selectedSlot || !modality || !psychId) return;
    setBooking(selectedSlot);
    try {
      await portalApi.createAppointment({ psychologistId: psychId, startUtc: selectedSlot, modality });
      setSelectedSlot(null);
      setDate("");
      setSuccessOpen(true);
    } catch (e) {
      toast.error(e.message || "No se pudo agendar");
    } finally {
      setBooking(null);
    }
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.role === "admin") return <Navigate to="/admin" replace />;
  if (user?.role === "psychologist") return <Navigate to="/psicologo" replace />;
  if (user?.role !== "client") return <Navigate to="/login" replace />;

  const step1 = Boolean(modality);
  const step2 = Boolean(psychId);
  const step3 = Boolean(date);
  const cm = selectedPsych?.consultationModes || CM_BOTH;
  const showPresencialExtra = modality === CM_IN_PERSON;
  const psychName = selectedPsych?.name;
  const psychLocationLine = selectedPsych?.location ? formatMexicoLocation(selectedPsych.location) : "";
  const presencialLocationComplete = modality === CM_IN_PERSON && Boolean(patientLocation.stateCode && patientLocation.municipalityCode);
  const showPsychDropdown = modality === CM_VIRTUAL || (modality === CM_IN_PERSON && presencialLocationComplete);

  return (
    <PortalShell
      wide
      title="Agendar cita"
      subtitle={
        modality === CM_IN_PERSON
          ? "Indica primero tu ciudad; luego elige el especialista disponible en esa zona, día y horario (hora Ciudad de México)."
          : "Elige modalidad y especialista; después un día hábil y un horario disponible."
      }
      headerBackgroundImage="/images/agendar-hero.jpg"
    >
      {/* Steps */}
      <div className="flex flex-wrap gap-2 mb-6 sm:mb-8" aria-hidden>
        <StepBadge n={1} label="Modalidad" done={step1} />
        <StepBadge n={2} label="Especialista" done={step2} />
        <StepBadge n={3} label="Fecha" done={step3} />
        <StepBadge n={4} label="Horario" done={Boolean(selectedSlot)} />
      </div>

      <div className="space-y-6 w-full min-w-0">

        {/* ── Step 1: Modality ── */}
        <SectionCard>
          <p className="text-sm font-semibold text-brand-dark mb-1">1 · Modalidad de la sesión</p>
          <p className="text-sm text-brand-gray mb-4 leading-snug">
            Elige si tu cita será presencial o en línea. Para presencial deberás indicar tu ciudad primero.
          </p>
          <div className="grid sm:grid-cols-2 gap-3" role="radiogroup" aria-label="Modalidad">
            {[
              {
                value: CM_IN_PERSON,
                title: "Presencial",
                desc: "En la ubicación que indique el profesional.",
              },
              {
                value: CM_VIRTUAL,
                title: "En línea (virtual)",
                desc: "Videollamada; el profesional te contactará.",
              },
            ].map((opt) => (
              <label
                key={opt.value}
                className={`flex cursor-pointer rounded-xl border-2 p-4 gap-3 transition-colors ${
                  modality === opt.value
                    ? "border-brand-navy bg-brand-navy/5"
                    : "border-brand-lavender/20 bg-white hover:border-brand-lavender/40"
                }`}
              >
                <input
                  type="radio"
                  name="booking-modality"
                  className="mt-0.5 accent-brand-navy"
                  checked={modality === opt.value}
                  onChange={() => setModality(opt.value)}
                />
                <span>
                  <span className="block text-sm font-semibold text-brand-dark">{opt.title}</span>
                  <span className="block text-xs text-brand-gray mt-0.5 leading-snug">{opt.desc}</span>
                </span>
              </label>
            ))}
          </div>
        </SectionCard>

        {/* ── Step 2: Specialist ── */}
        {modality && (
          <SectionCard>
            <div className="flex items-center gap-2 mb-1">
              <UserCircleIcon className="h-4 w-4 text-brand-lavender shrink-0" aria-hidden />
              <p className="text-sm font-semibold text-brand-dark">2 · Especialista</p>
            </div>
            <p className="text-xs text-brand-gray mb-4 leading-snug">
              Modalidad:{" "}
              <span className="font-medium text-brand-dark">{labelAppointmentModality(modality)}</span>
              {modality === CM_VIRTUAL
                ? ". Solo aparecen profesionales que ofrecen sesión en línea."
                : ". Indica tu ubicación para ver especialistas en esa ciudad."}
            </p>

            {/* Location picker for presencial */}
            {modality === CM_IN_PERSON && (
              <div className="rounded-xl border border-brand-lavender/15 bg-brand-beige/50 p-4 sm:p-5 mb-4 space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-brand-dark">
                  <MapPinIcon className="h-4 w-4 text-brand-lavender shrink-0" aria-hidden />
                  ¿En qué ciudad te encuentras?
                </div>
                <p className="text-xs text-brand-gray leading-relaxed">
                  Solo aparecen estados y municipios donde hay especialistas con consulta presencial registrada.
                </p>
                <MexicoLocationSelects
                  value={patientLocation}
                  onChange={setPatientLocation}
                  disabled={loadingPsychs}
                  stateCodesFilter={loadingPsychs ? null : registeredStateCodes}
                  municipalityCodesFilter={
                    loadingPsychs || !patientLocation.stateCode ? null : registeredMunicipalityCodes
                  }
                />
                {!loadingPsychs && registeredStateCodes.length === 0 && (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200/80 rounded-lg px-3 py-2">
                    Por el momento no hay consultas presenciales registradas. Puedes elegir sesión en línea.
                  </p>
                )}
              </div>
            )}

            {/* Specialist dropdown */}
            {!showPsychDropdown ? null : loadingPsychs ? (
              <div className="flex items-center gap-2 text-brand-gray text-sm py-2">
                <div className="h-4 w-4 rounded-full border-2 border-brand-lavender/30 border-t-brand-navy animate-spin" />
                Cargando especialistas…
              </div>
            ) : filteredPsychs.length === 0 ? (
              modality === CM_IN_PERSON && presencialLocationComplete ? (
                <div className="rounded-xl border border-amber-200/80 bg-amber-50/80 p-4 sm:p-5 space-y-3">
                  <p className="text-sm font-semibold text-brand-dark">
                    Sin especialistas en esa ubicación por el momento.
                  </p>
                  <p className="text-sm text-brand-gray leading-relaxed">
                    Te recomendamos agendar una sesión en línea.
                  </p>
                  <button
                    type="button"
                    onClick={() => setModality(CM_VIRTUAL)}
                    className="rounded-xl border border-brand-navy text-brand-navy px-4 py-2 text-sm font-semibold hover:bg-brand-navy/5 transition-colors"
                  >
                    Cambiar a sesión en línea
                  </button>
                </div>
              ) : (
                <p className="text-sm text-brand-gray py-6 border border-dashed border-brand-lavender/25 rounded-xl text-center">
                  No hay profesionales disponibles para esta modalidad.
                </p>
              )
            ) : (
              <PortalSelect
                size="lg"
                className={selectClsLg}
                value={psychId}
                onChange={(e) => setPsychId(e.target.value)}
                aria-label="Seleccionar psicólogo"
              >
                <option value="">Selecciona un profesional</option>
                {filteredPsychs.map((p) => (
                  <option key={p.psychologistId} value={p.psychologistId}>
                    {p.name}
                    {p.consultationModes === CM_BOTH ? " · Presencial y virtual" : ""}
                  </option>
                ))}
              </PortalSelect>
            )}

            {/* Selected psych info */}
            {psychId && selectedPsych && (
              <div className="mt-4 rounded-xl border border-brand-lavender/15 bg-brand-beige/40 p-4 flex gap-4">
                <div className="shrink-0">
                  {selectedPsych.profileImageUrl ? (
                    <img
                      src={selectedPsych.profileImageUrl}
                      alt=""
                      className="h-20 w-20 rounded-xl object-cover border border-white shadow-sm"
                    />
                  ) : (
                    <div className="h-20 w-20 rounded-xl border border-brand-lavender/15 bg-white flex items-center justify-center text-brand-gray shadow-sm">
                      <UserCircleIcon className="h-10 w-10" />
                    </div>
                  )}
                </div>
                <div className="min-w-0 space-y-1">
                  <p className="text-sm font-semibold text-brand-dark">{selectedPsych.name}</p>
                  <p className="text-xs text-brand-gray">
                    {labelConsultationModesSetting(cm)}
                  </p>
                  {selectedPsych.specialtyDescription && (
                    <p className="text-sm text-brand-gray leading-relaxed">
                      {selectedPsych.specialtyDescription}
                    </p>
                  )}
                  {showPresencialExtra && psychLocationLine && (
                    <p className="text-xs text-brand-gray pt-0.5">
                      Consulta:{" "}
                      <span className="font-medium text-brand-dark">{psychLocationLine}</span>
                    </p>
                  )}
                  {modality === CM_VIRTUAL && (
                    <p className="text-xs text-brand-gray pt-0.5">
                      El profesional te contactará para coordinar el enlace de la sesión.
                    </p>
                  )}
                </div>
              </div>
            )}
          </SectionCard>
        )}

        {/* ── Steps 3 & 4: Date + Time ── */}
        {psychId && modality && (
          <div className="grid lg:grid-cols-2 gap-6 items-start">
            <SectionCard>
              <p className="text-sm font-semibold text-brand-dark mb-4">3 · Fecha</p>
              <BookingMonthPicker valueYmd={date} onChangeYmd={setDate} />
            </SectionCard>

            <SectionCard>
              <p className="text-sm font-semibold text-brand-dark mb-1">4 · Horario</p>
              {psychName && (
                <p className="text-xs text-brand-gray mb-4 leading-snug">
                  <span className="font-medium text-brand-dark">{psychName}</span>
                  {" · "}
                  {labelAppointmentModality(modality)}
                  {date ? ` · ${date}` : " · elige una fecha"}
                </p>
              )}
              {!date && (
                <p className="text-sm text-brand-gray py-10 border border-dashed border-brand-lavender/25 rounded-xl text-center">
                  Selecciona una fecha disponible para ver los horarios.
                </p>
              )}
              {date && (
                <TimeSlotGrid
                  slotOptions={slotOptions}
                  loading={loading}
                  selectedSlot={selectedSlot}
                  onSelect={setSelectedSlot}
                  disabledIso={booking}
                  disableAll={Boolean(booking)}
                />
              )}
              {date && !loading && slotOptions.length === 0 && (
                <div className="rounded-xl border border-dashed border-brand-lavender/25 py-8 text-center px-4">
                  <p className="text-sm font-semibold text-brand-dark">Sin horarios este día</p>
                  <p className="text-xs text-brand-gray mt-1 leading-snug">
                    Puede ser día inhábil o fuera de la agenda del profesional. Prueba otra fecha.
                  </p>
                </div>
              )}
            </SectionCard>
          </div>
        )}

        {/* Guidance hints */}
        {!modality && (
          <p className="text-sm text-brand-gray text-center py-6">
            Primero elige la modalidad de tu sesión (presencial o en línea).
          </p>
        )}
        {modality && !psychId && showPsychDropdown && filteredPsychs.length > 0 && (
          <p className="text-sm text-brand-gray text-center py-6">
            Elige un especialista para ver el calendario y los horarios disponibles.
          </p>
        )}
      </div>

      {/* ── Floating confirm bar ── */}
      {psychId && date && selectedSlot && modality && (
        <div className="fixed bottom-0 left-0 right-0 z-40 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] pointer-events-none">
          <div className="max-w-5xl mx-auto pointer-events-auto">
            <div className="rounded-2xl border border-white/10 bg-brand-navy text-white shadow-2xl shadow-black/30 px-5 py-4 sm:px-6 sm:py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="min-w-0">
                <p className="text-xs text-white/50 uppercase tracking-wide mb-0.5">
                  Horario elegido · {labelAppointmentModality(modality)}
                </p>
                <p className="font-semibold text-white leading-snug truncate">
                  {formatMxLong(selectedSlot)}
                </p>
              </div>
              <button
                type="button"
                disabled={Boolean(booking)}
                onClick={confirmBooking}
                className="shrink-0 w-full sm:w-auto min-w-[200px] rounded-xl bg-white text-brand-navy font-semibold py-3 px-6 text-sm shadow-lg hover:brightness-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {booking ? "Agendando…" : "Confirmar cita"}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedSlot && <div className="h-28 sm:h-24" aria-hidden />}

      <SuccessModal open={successOpen} onClose={handleSuccessClose} />
    </PortalShell>
  );
}
